// /opt/isp_erp/frontend/src/pages/admin/clientes/UsuariosClientes.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "../../../api/axios";
import { useAuth } from '../../../auth/useAuth';
import { Link } from "react-router-dom";
import "./UsuariosClientes.css";

export default function UsuariosClientes() {
  const { canExport } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [showEliminarModal, setShowEliminarModal] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [mensajeEliminacion, setMensajeEliminacion] = useState("");

  // Estados para búsqueda y paginación
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Cargar clientes al montar el componente
  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/customers");
      console.log("Clientes cargados:", res.data);
      setClientes(res.data);
    } catch (err) {
      console.error("Error al cargar clientes:", err);
      alert(err.response?.data?.detail || "Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  // ===== FUNCIÓN: Exportar clientes a CSV =====
  const exportarClientesCSV = () => {
    if (!clientes || clientes.length === 0) {
      alert("No hay datos de clientes para exportar");
      return;
    }

    const columnas = [
      "id",
      "tipo_identificacion",
      "identificacion",
      "nombres",
      "apellidos",
      "telefono",
      "email",
      "direccion",
      "ciudad",
      "servicio",
      "plan",
      "estado",
      "ficha",
      "vineta",
      "onu_id",
      "created_at",
      "updated_at"
    ];

    const encabezados = columnas.join(",");
    const filas = clientes.map(cliente => {
      return columnas.map(col => {
        let valor = cliente[col] !== null && cliente[col] !== undefined ? cliente[col] : "";
        if (typeof valor === "string" && (valor.includes(",") || valor.includes("\n") || valor.includes('"'))) {
          valor = valor.replace(/"/g, '""');
          valor = `"${valor}"`;
        }
        return valor;
      }).join(",");
    }).join("\n");

    const contenidoCSV = `\uFEFF${encabezados}\n${filas}`;
    const blob = new Blob([contenidoCSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `clientes_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Filtrar clientes por estado Y búsqueda
  const clientesFiltrados = useMemo(() => {
    let filtrados = filtroEstado === "todos"
      ? clientes
      : clientes.filter(c => c.estado === filtroEstado);

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtrados = filtrados.filter(c => {
        return (
          c.id?.toString().includes(term) ||
          c.identificacion?.toLowerCase().includes(term) ||
          c.nombres?.toLowerCase().includes(term) ||
          c.apellidos?.toLowerCase().includes(term) ||
          c.telefono?.toLowerCase().includes(term) ||
          c.email?.toLowerCase().includes(term) ||
          c.direccion?.toLowerCase().includes(term) ||
          c.ciudad?.toLowerCase().includes(term) ||
          c.servicio?.toLowerCase().includes(term) ||
          c.plan?.toLowerCase().includes(term) ||
          c.estado?.toLowerCase().includes(term) ||
          c.vineta?.toLowerCase().includes(term) ||
          c.ficha?.toLowerCase().includes(term)
        );
      });
    }

    return filtrados;
  }, [clientes, filtroEstado, searchTerm]);

  // Paginación
  const totalPages = Math.ceil(clientesFiltrados.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentClientes = clientesFiltrados.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  // 🟢 FUNCIÓN PARA RETIRAR CLIENTE - VERSIÓN MEJORADA CON MENSAJES CLAROS
  const handleRetirarCliente = async (cliente) => {
    if (!window.confirm(`¿Está seguro de retirar al cliente ${cliente.nombres} ${cliente.apellidos}?`)) {
      return;
    }

    setProcesando(true);
    try {
      await axios.post(`/customers/${cliente.id}/retirar`, {
        motivo: "Retiro voluntario"
      });

      alert("✅ Cliente retirado exitosamente");
      fetchClientes();
    } catch (error) {
      console.error("Error retirando cliente:", error);

      if (error.response?.data) {
        const data = error.response.data;

        // Construir mensaje amigable
        let mensajeUsuario = "❌ No se pudo retirar el cliente\n\n";

        // Si el error tiene la estructura detallada del backend
        if (data.detail && typeof data.detail === 'object') {
          const detalle = data.detail;

          mensajeUsuario += `${detalle.error || 'Error desconocido'}\n`;

          // Mostrar facturas pendientes si existen
          if (detalle.facturas_pendientes && detalle.facturas_pendientes.length > 0) {
            mensajeUsuario += `\n📋 FACTURAS PENDIENTES (${detalle.cantidad}):\n`;
            mensajeUsuario += `💰 Total adeudado: $${detalle.total_pendiente?.toFixed(2)}\n\n`;

            detalle.facturas_pendientes.forEach((factura, index) => {
              const fecha = new Date(factura.fecha_emision).toLocaleDateString();
              mensajeUsuario += `${index + 1}. Factura del ${fecha}\n`;
              mensajeUsuario += `   💵 Monto: $${factura.total?.toFixed(2)}\n`;
              mensajeUsuario += `   📍 Estado: ${factura.estado}\n`;
              mensajeUsuario += `   🆔 ID: ${factura.id}\n\n`;
            });

            mensajeUsuario += `✅ ${detalle.accion_requerida || 'Pague o anule las facturas pendientes'}`;
          }

          // Mostrar promesas activas si existen
          else if (detalle.promesas_activas && detalle.promesas_activas.length > 0) {
            mensajeUsuario += `\n📅 PROMESAS DE PAGO ACTIVAS (${detalle.cantidad}):\n`;
            mensajeUsuario += `💰 Total comprometido: $${detalle.total_promesas?.toFixed(2)}\n\n`;

            detalle.promesas_activas.forEach((promesa, index) => {
              const fecha = new Date(promesa.fecha_compromiso).toLocaleDateString();
              mensajeUsuario += `${index + 1}. Promesa del ${fecha}\n`;
              mensajeUsuario += `   💵 Monto: $${promesa.monto?.toFixed(2)}\n`;
              mensajeUsuario += `   📍 Estado: ${promesa.estado}\n`;
              mensajeUsuario += `   🆔 ID: ${promesa.id}\n\n`;
            });

            mensajeUsuario += `✅ ${detalle.accion_requerida || 'Cumpla o anule las promesas pendientes'}`;
          }
        }
        // Si es un error simple
        else if (data.detail) {
          mensajeUsuario += data.detail;
        }
        // Si es otro tipo de error
        else {
          mensajeUsuario += JSON.stringify(data, null, 2);
        }

        alert(mensajeUsuario);

      } else {
        alert("❌ Error de conexión. Verifique su red e intente nuevamente.");
      }
    } finally {
      setProcesando(false);
    }
  };

  // Función para eliminar cliente (solo retirados)
  const handleEliminarCliente = async () => {
    if (!clienteToDelete) return;

    setProcesando(true);
    setMensajeEliminacion("");
    try {
      const response = await axios.delete(`/customers/${clienteToDelete.id}`);
      setMensajeEliminacion(response.data.instalaciones_eliminadas
        ? `✅ Cliente eliminado. También se eliminaron ${response.data.instalaciones_eliminadas} instalaciones asociadas.`
        : "✅ Cliente eliminado permanentemente");

      setShowEliminarModal(false);
      setClienteToDelete(null);
      fetchClientes();

      setTimeout(() => {
        alert(mensajeEliminacion || "✅ Cliente eliminado permanentemente");
      }, 100);
    } catch (error) {
      console.error("Error eliminando cliente:", error);
      alert(error.response?.data?.detail || "Error al eliminar cliente");
    } finally {
      setProcesando(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando clientes...</p>
      </div>
    );
  }

  return (
    <div className="clientes-container">
      <div className="clientes-header">
        <div>
          <h1>Usuarios Clientes</h1>
          <p className="subtitle">Gestione todos los usuarios del sistema</p>
        </div>
        {canExport() && (
          <button
            onClick={exportarClientesCSV}
            className="btn-export"
          >
            📥 Exportar CSV
          </button>
        )}
      </div>

      {/* Barra de búsqueda y filtros */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar por ID, identificación, nombre, plan, viñeta, ficha..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="clear-search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="filters-group">
          <select
            value={filtroEstado}
            onChange={(e) => {
              setFiltroEstado(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="todos">Todos los estados</option>
            <option value="ACTIVO">✅ Activos</option>
            <option value="SUSPENDIDO">⏸️ Suspendidos</option>
            <option value="RETIRADO">📦 Retirados</option>
          </select>

          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="per-page-select"
          >
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
            <option value={250}>250 por página</option>
          </select>

          <span className="results-count">
            {clientesFiltrados.length} resultados
          </span>
        </div>
      </div>

      {/* Tabla de clientes */}
      {clientesFiltrados.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>{searchTerm ? "No se encontraron clientes" : "No hay clientes para mostrar"}</h3>
          <p>
            {searchTerm
              ? `No hay resultados para "${searchTerm}"`
              : "Prueba cambiando el filtro de estado"}
          </p>
          {searchTerm && (
            <button onClick={clearSearch} className="btn-link">
              Limpiar búsqueda
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="clientes-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Contacto</th>
                  <th>Servicio/Plan</th>
                  <th>Viñeta</th>
                  <th>Ficha</th>
                  <th>Estado</th>
                  <th>IP Asignada</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentClientes.map((cliente) => (
                  <tr key={cliente.id}>
                    <td className="id-cell">#{cliente.id}</td>
                    <td>
                      <div className="cliente-info">
                        <div className="cliente-avatar">👤</div>
                        <div>
                          <div className="cliente-nombre">
                            {cliente.nombres} {cliente.apellidos}
                          </div>
                          <div className="cliente-identificacion">
                            {cliente.tipo_identificacion || 'CEDULA'}: {cliente.identificacion}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="contacto-info">
                        {cliente.telefono && (
                          <div className="contacto-item">
                            <span className="contacto-icon">📞</span>
                            {cliente.telefono}
                          </div>
                        )}
                        {cliente.email && (
                          <div className="contacto-item">
                            <span className="contacto-icon">✉️</span>
                            <span className="truncate">{cliente.email}</span>
                          </div>
                        )}
                        {cliente.direccion && (
                          <div className="contacto-item">
                            <span className="contacto-icon">📍</span>
                            <span className="truncate">{cliente.direccion}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="servicio-info">
                        <div className="servicio-tipo">
                          {cliente.servicio === 'INTERNET' && '🌐'}
                          {cliente.servicio === 'TV' && '📺'}
                          {cliente.servicio === 'DUOPACK' && '📦'}
                          <span>{cliente.servicio || 'N/A'}</span>
                        </div>
                        <div className="servicio-plan">{cliente.plan || 'Sin plan'}</div>
                      </div>
                    </td>
                    <td>
                      {cliente.vineta ? (
                        <div className="vineta-cell" title={cliente.vineta}>
                          <span className="vineta-icon">📌</span>
                          <span className="truncate">{cliente.vineta}</span>
                        </div>
                      ) : (
                        <span className="empty-value">—</span>
                      )}
                    </td>
                    <td>
                      {cliente.ficha ? (
                        <div className="ficha-cell" title={cliente.ficha}>
                          <span className="ficha-icon">📄</span>
                          <span className="truncate">{cliente.ficha}</span>
                        </div>
                      ) : (
                        <span className="empty-value">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`estado-badge estado-${cliente.estado?.toLowerCase()}`}>
                        {cliente.estado === 'ACTIVO' && '✅ '}
                        {cliente.estado === 'SUSPENDIDO' && '⏸️ '}
                        {cliente.estado === 'RETIRADO' && '📦 '}
                        {cliente.estado}
                      </span>
                    </td>
                    <td>
                      {cliente.ip_asignada ? (
                        <span className="ip-badge">{cliente.ip_asignada}</span>
                      ) : (
                        <span className="empty-value">—</span>
                      )}
                    </td>
                    <td>
                      <div className="acciones-cell">
                        <Link
                          to={`/clientes/detalle/${cliente.id}`}
                          className="btn-accion ver"
                        >
                          Ver
                        </Link>

                        {cliente.estado === 'ACTIVO' && (
                          <button
                            className="btn-accion retirar"
                            onClick={() => handleRetirarCliente(cliente)}
                            disabled={procesando}
                          >
                            Retirar
                          </button>
                        )}

                        {cliente.estado === 'RETIRADO' && (
                          <button
                            className="btn-accion eliminar"
                            onClick={() => {
                              setClienteToDelete(cliente);
                              setShowEliminarModal(true);
                            }}
                            disabled={procesando}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Página {currentPage} de {totalPages}
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                  title="Primera página"
                >
                  ⏮️
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                  title="Página anterior"
                >
                  ◀️
                </button>

                <div className="pagination-numbers">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                  title="Página siguiente"
                >
                  ▶️
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                  title="Última página"
                >
                  ⏭️
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal para confirmar eliminación */}
      {showEliminarModal && clienteToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">⚠️ Eliminar Cliente Permanentemente</h3>
            <p className="modal-text">
              ¿Está seguro que desea eliminar permanentemente al cliente?
            </p>
            <div className="modal-info">
              <p className="font-medium">{clienteToDelete.nombres} {clienteToDelete.apellidos}</p>
              <p className="text-sm">ID: {clienteToDelete.identificacion}</p>
              <p className="text-sm">Plan: {clienteToDelete.plan || 'N/A'}</p>
            </div>
            <p className="modal-warning">
              Esta acción no se puede deshacer.
              Se eliminarán todos los datos del cliente y las instalaciones asociadas.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowEliminarModal(false);
                  setClienteToDelete(null);
                }}
                className="modal-btn cancel"
                disabled={procesando}
              >
                Cancelar
              </button>
              <button
                onClick={handleEliminarCliente}
                className="modal-btn delete"
                disabled={procesando}
              >
                {procesando ? "Eliminando..." : "Eliminar Permanentemente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
